import { I18n } from 'i18n-js'
import type { ReactNode } from 'react'
import { Children, useEffect } from 'react'
import en from './locale/en.json'
import es from './locale/es.json'
import { compiler } from 'markdown-to-jsx/react'

const i18n = new I18n({
  en,
  es
})
i18n.defaultLocale = 'en'
i18n.locale = 'en'
i18n.enableFallback = true

// get localized text from key, as a plain string
export const tStr = (localeKey: string, params: Record<string, string | number> = undefined): string => {
  return i18n.t(localeKey, params)
}

// get localized text from key, as renderable jsx
// use tStr instead when the value has to be a plain string (placeholder, alt, title)
export const t = (localeKey: string, params: Record<string, ReactNode> = undefined): ReactNode => {
  // first replace any jsx objects in the params with a temporary string
  const safeParams = {}
  const safeNeeded = []
  if (params) {
    Object.keys(params).forEach(key => {
      if (typeof params[key] === 'object') {
        safeParams[key] = `tmp_${key}`
        safeNeeded.push(key)
      } else {
        safeParams[key] = params[key]
      }
    })
  }

  // translate string
  const str = i18n.t(localeKey, safeParams)

  // convert any markdown to jsx, always as a list of nodes so it can be spliced below.
  // disableAutoLink stops bare email addresses and urls from being turned into anchors
  let content: ReactNode[] = [compiler(str, { wrapper: null, disableAutoLink: true })].flat()

  // replace temporary strings with the jsx passed in params
  safeNeeded.forEach(key => {
    const tmpKey = `tmp_${key}`
    const i = content.findIndex(part => typeof part === 'string' && part.includes(tmpKey))
    if (i !== -1) {
      const part = content[i] as string
      const at = part.indexOf(tmpKey)
      content = [
        ...content.slice(0, i),
        part.slice(0, at),
        params[key],
        part.slice(at + tmpKey.length),
        ...content.slice(i + 1)
      ]
    }
  })

  if (content.length === 1) {
    return content[0]
  }
  // keyed so callers can render the list of nodes directly as children
  return Children.toArray(content)
}

// get the highest ranked supported language from the user or default to english
export const getUserLang = () => {
  try {
    return (
      navigator?.languages
        .find(lang => {
          if (lang.indexOf('en') === 0) return 'en'
          if (lang.indexOf('es') === 0) return 'es'
          return false
        })
        .split('-')[0] || 'en'
    )
  } catch {
    return 'en'
  }
}
// change language, accepts 'en' or 'es'
// if not passed, the language is retrieved from the browser
export const setLang = (lang = getUserLang()) => {
  // set the translation language
  i18n.locale = lang
}

export const LanguageWrapper = ({ children }) => {
  useEffect(() => {
    setLang()
  }, [])

  return children
}

export default i18n
